'use client'

import { useState } from 'react'

import type { BrandLabCategory, BrandLabProduct } from '@/lib/supabase/brand-modules/types'

import type { LabFit } from './lab-fit-toggle'

export type LabFitScore = BrandLabCategory & { score: number }

const legacyFitFor = (product: BrandLabProduct): LabFit =>
  product.environments.some((environment) => environment.tunedUrl || environment.tunedScore != null)
    ? 'tuned'
    : 'initial'

const scoreForFit = (category: BrandLabCategory, fit: LabFit, legacyFit: LabFit): number | null => {
  const fitScore = fit === 'initial' ? category.initialScore : category.tunedScore
  return fitScore ?? (fit === legacyFit ? category.score : null) ?? null
}

// Fit selection shared by the compact and wide lab score cards: per-fit score
// lists (legacy single-score bakes count for the fit they were recorded
// under), defaulting to tuned and falling back to whichever fit has data.
export const useLabFitScores = (product: BrandLabProduct) => {
  const [selectedFit, setSelectedFit] = useState<LabFit>('tuned')
  const legacyFit = legacyFitFor(product)
  const scoreEntries = (fit: LabFit): LabFitScore[] =>
    product.categories.flatMap((category) => {
      const score = scoreForFit(category, fit, legacyFit)
      return score == null ? [] : [{ ...category, score }]
    })
  const scores: Record<LabFit, LabFitScore[]> = { initial: scoreEntries('initial'), tuned: scoreEntries('tuned') }
  const activeFit: LabFit = scores[selectedFit].length > 0 ? selectedFit : selectedFit === 'tuned' ? 'initial' : 'tuned'
  return { scores, activeFit, setSelectedFit }
}
